'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect, useRef } from 'react'

export { ErrorBoundary } from '@sentry/nextjs'

/**
 * Report a React error boundary error to Sentry exactly once per error.
 *
 * The Next.js server `digest` is preserved as a correlation tag while the
 * actual `Error` is captured. No user identity or other PII is attached.
 * The ref guard prevents duplicate captures when the boundary re-renders
 * (e.g. auth state resolving) while React Strict Mode remounts effects.
 */
export const useReportErrorOnce = (error: Error & { digest?: string }): void => {
  const reportedErrorsRef = useRef(new WeakSet<Error>())

  useEffect(() => {
    if (reportedErrorsRef.current.has(error)) return

    reportedErrorsRef.current.add(error)

    Sentry.withScope(scope => {
      if (error.digest) {
        scope.setTag('digest', error.digest)
      }

      scope.setLevel('error')
      Sentry.captureException(error)
    })
  }, [error])
}
